import React, { useEffect, useState } from "react";
import api from "../api";
import FileAttachment from "../components/FileAttachment";
import DecimalInput from "../components/DecimalInput";
import IntegerInput from "../components/IntegerInput";
import MaskedInput from "../components/MaskedInput";
import jsPDF from "jspdf";

export default function CompletedRequestView({ instance, step }) {
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [histRes, formRes] = await Promise.all([
          api.get(`/processes/instances/${instance.id}/history`),
          instance.process.formId
            ? api.get(`/forms/${instance.process.formId}`)
            : Promise.resolve({ data: null }),
        ]);
        setHistory(histRes.data.steps || []);
        setForm(formRes.data);
      } catch {
        setForm(null);
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };
    if (instance) {
      setLoading(true);
      fetchData();
    }
  }, [instance]);

  if (!instance) return null;
  if (loading) return <p>Carregando...</p>;
  if (!form) return <p>Nenhum formulário para exibir.</p>;

  const fields = form.schema?.fields || [];
  const groups = form.schema?.groups || [];
  const fieldMap = fields.reduce((acc, f) => ({ ...acc, [f.id]: f }), {});

  const limit =
    typeof step === "number" && step >= 0
      ? Math.min(step + 1, history.length)
      : history.length;
  let aggregated = {};
  for (let i = 0; i < limit; i++) {
    aggregated = { ...aggregated, ...(history[i].data || {}) };
  }

  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    doc.setLineWidth(0.1);
    const margin = 10;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;
    const colWidth = contentWidth / 12;
    const headerY = margin;

    doc.setFontSize(14);
    doc.text(
      `Solicitação ${instance.id} - ${instance.process.name}`,
      margin,
      headerY,
    );
    doc.setFontSize(11);
    const requester =
      instance.requester?.name ||
      instance.user?.name ||
      instance.startedBy?.name ||
      "-";
    const headerLines = [
      `ID: ${instance.id}`,
      `Processo: ${instance.process.name}`,
      `Solicitante: ${requester}`,
      `Início: ${new Date(instance.startedAt).toLocaleString()}`,
    ];
    headerLines.forEach((l, idx) => {
      doc.text(l, margin, headerY + 8 + idx * 6);
    });

    let x = margin;
    let y = headerY + 8 + headerLines.length * 6 + 2;
    const rowHeight = 18;
    const rowSpacing = 0;
    const groupSpacing = 2;
    let remaining = 12;

    const groups = form.schema?.groups || [];
    const ungrouped = {
      id: "__ungrouped__",
      label: groups.length > 0 ? "Campos" : "",
    };

    const orderedGroups = [...groups, ungrouped];

    orderedGroups.forEach((g) => {
      const gFields = fields
        .filter(
          (f) => (f.groupId || "__ungrouped__") === (g.id || "__ungrouped__"),
        )
        .filter((f) => Object.prototype.hasOwnProperty.call(aggregated, f.id));
      if (gFields.length === 0) return;

      if (g.label) {
        doc.setFontSize(12);
        // draw group header without bottom line to avoid thicker borders
        doc.line(margin, y, margin + contentWidth, y);
        doc.line(margin, y, margin, y + 8);
        doc.line(margin + contentWidth, y, margin + contentWidth, y + 8);
        doc.text(g.label, margin + contentWidth / 2, y + 5, {
          align: "center",
        });
        y += 8; // header should touch first field row
      }

      x = margin;
      remaining = 12;

      gFields.forEach((f) => {
        const size = f.size || 12;
        const width = size * colWidth;

        if (size > remaining) {
          x = margin;
          y += rowHeight + rowSpacing;
          remaining = 12;
        }

        let val = aggregated[f.id];
        if (val && typeof val === "object") {
          if (val.name) val = val.name;
          else val = JSON.stringify(val);
        }

        doc.rect(x, y, width, rowHeight);
        doc.setFontSize(10);
        doc.text(f.label, x + 2, y + 5);
        doc.setFontSize(12);
        const lines = doc.splitTextToSize(String(val ?? ""), width - 4);
        doc.text(lines, x + 2, y + 12);

        x += width; // keep fields connected
        remaining -= size;
      });

      y += rowHeight + groupSpacing;
    });

    doc.save(`solicitacao-${instance.id}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl">
          Solicitação {instance.id} - {instance.process.name}
        </h1>
        <button
          onClick={handleDownloadPdf}
          className="bg-blue-600 text-white px-3 py-1 rounded"
        >
          Baixar PDF
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2 bg-gray-50 p-2 rounded shadow text-sm">
        <div>
          <span className="font-medium">ID:</span> {instance.id}
        </div>
        <div>
          <span className="font-medium">Processo:</span> {instance.process.name}
        </div>
        <div>
          <span className="font-medium">Solicitante:</span>{" "}
          {instance.requester?.name ||
            instance.user?.name ||
            instance.startedBy?.name ||
            "-"}
        </div>
        <div>
          <span className="font-medium">Início:</span>{" "}
          {new Date(instance.startedAt).toLocaleString()}
        </div>
      </div>
      {groups.map((g) => {
        const gFields = fields.filter(
          (f) =>
            f.groupId === g.id &&
            Object.prototype.hasOwnProperty.call(aggregated, f.id),
        );
        if (gFields.length === 0) return null;
        return (
          <div key={g.id} className="mb-4">
            <h3 className="text-lg font-medium mb-1">{g.label}</h3>
            <div className="grid grid-cols-12 gap-2 bg-gray-50 p-4 rounded shadow">
              {gFields.map((f, idx) => (
                <div
                  key={`${g.id}-${idx}`}
                  className="flex flex-col"
                  style={{ gridColumn: `span ${f.size} / span ${f.size}` }}
                >
                  <label className="font-medium">
                    {f.label}
                    {f.required && <span className="text-red-600"> *</span>}
                  </label>
                  {f.type === "list" ? (
                    <select
                      className="border px-2 py-1 bg-gray-100"
                      value={aggregated[f.id] || ""}
                      disabled
                    >
                      <option value="" disabled>
                        Selecione...
                      </option>
                      {f.options &&
                        f.options.map((o, i) => (
                          <option key={i} value={o}>
                            {o}
                          </option>
                        ))}
                    </select>
                  ) : f.type === "file" ? (
                    <FileAttachment file={aggregated[f.id]} />
                  ) : f.type === "decimal" ? (
                    <DecimalInput
                      decimalPlaces={f.decimalPlaces || 0}
                      value={aggregated[f.id]}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "number" ? (
                    <IntegerInput
                      value={aggregated[f.id]}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "phone" ? (
                    <MaskedInput
                      mask="phone"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cpf" ? (
                    <MaskedInput
                      mask="cpf"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cnpj" ? (
                    <MaskedInput
                      mask="cnpj"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cep" ? (
                    <MaskedInput
                      mask="cep"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "email" ? (
                    <input
                      type="email"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      inputMode="email"
                      autoComplete="email"
                      pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "date" ? (
                    <input
                      type="date"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "time" ? (
                    <input
                      type="time"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : (
                    <input
                      type={f.type}
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {fields.filter(
        (f) =>
          !f.groupId && Object.prototype.hasOwnProperty.call(aggregated, f.id),
      ).length > 0 && (
        <div className="mb-4">
          {groups.length > 0 && (
            <h3 className="text-lg font-medium mb-1">Campos</h3>
          )}
          <div className="grid grid-cols-12 gap-2 bg-gray-50 p-4 rounded shadow">
            {fields
              .filter(
                (f) =>
                  !f.groupId &&
                  Object.prototype.hasOwnProperty.call(aggregated, f.id),
              )
              .map((f, idx) => (
                <div
                  key={`nogroup-view-${idx}`}
                  className="flex flex-col"
                  style={{ gridColumn: `span ${f.size} / span ${f.size}` }}
                >
                  <label className="font-medium">
                    {f.label}
                    {f.required && <span className="text-red-600"> *</span>}
                  </label>
                  {f.type === "list" ? (
                    <select
                      className="border px-2 py-1 bg-gray-100"
                      value={aggregated[f.id] || ""}
                      disabled
                    >
                      <option value="" disabled>
                        Selecione...
                      </option>
                      {f.options &&
                        f.options.map((o, i) => (
                          <option key={i} value={o}>
                            {o}
                          </option>
                        ))}
                    </select>
                  ) : f.type === "file" ? (
                    <FileAttachment file={aggregated[f.id]} />
                  ) : f.type === "decimal" ? (
                    <DecimalInput
                      decimalPlaces={f.decimalPlaces || 0}
                      value={aggregated[f.id]}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "number" ? (
                    <IntegerInput
                      value={aggregated[f.id]}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "phone" ? (
                    <MaskedInput
                      mask="phone"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cpf" ? (
                    <MaskedInput
                      mask="cpf"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cnpj" ? (
                    <MaskedInput
                      mask="cnpj"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cep" ? (
                    <MaskedInput
                      mask="cep"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "email" ? (
                    <input
                      type="email"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      inputMode="email"
                      autoComplete="email"
                      pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "date" ? (
                    <input
                      type="date"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "time" ? (
                    <input
                      type="time"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : (
                    <input
                      type={f.type}
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
